// @ts-strict-ignore
/**
 * Brownfield Bridge Sagas - Event Forwarding to Native Bridge
 *
 * @todo Refactor to consolidate event watchers: As we add navigation and user interaction events,
 * having multiple separate watchers will increase complexity.
 * See: https://github.com/dialoguemd/shell-apps/blob/master/proposals/001-refactor-brownfield-event-watchers.md
 */

import { getNativeModule } from 'APP/BrownfieldBridge/utils'
import * as BrownfieldBridgeInternal from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import type { KeepAliveTimingConfig, BrownfieldBridgeEvent } from 'APP/BrownfieldBridge/types'
import { take, fork, put, race, throttle, call, select } from 'redux-saga/effects'
import type {
  KeepAliveVideoCall,
  KeepAliveUserInteraction,
  KeepAliveNavigation,
  FetchOneEpisodeSuccess,
  FetchOneEpisodeFailure,
} from 'APP/BrownfieldBridge/eventTypes'

import { StartupTypes } from 'APP/Redux/StartupRedux'
import { LoginTypes } from 'APP/Redux/LoginRedux'
import { PatientTypes } from 'APP/Redux/PatientRedux'
import { NotificationTypes } from 'APP/Redux/NotificationRedux'
import { featuresActions } from 'APP/Redux/FeaturesSlice'
import { PatientHistoryTypes } from 'APP/Redux/PatientHistoryRedux'
import { videoSessionActions } from 'APP/Store/VideoSession'
import {
  selectKeepAliveIntervalId,
  selectVideoCallAcceptedAt,
} from 'APP/Store/VideoSession/selectors'
import Config from 'APP/Config'
import { createAction, PayloadAction } from '@reduxjs/toolkit'
import { BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE } from 'APP/BrownfieldBridge/deeplinkTypes'
import { mediaPlaybackStarted, mediaPlaybackStopped } from 'APP/Redux/MediaRedux'
import { KEEPALIVE_CLEANUP_ACTIONS } from 'APP/Store/VideoSession/slice'
import { selectSkipTokenExchange } from 'APP/Store/Brownfield'
import { TypedRoute } from 'APP/Nav/types'
import { logger } from 'APP/Lib/Datadog'
import type { ConversationEpisode } from 'APP/Services/Silkroad/types'

/**
 * Redux action for triggering user interaction keepalive signals.
 *
 * This action is dispatched by the UserInteractionWatcher component when user
 * touch events are detected. The corresponding saga throttles these events and
 * forwards them to the brownfield bridge to maintain the host app's session.
 *
 * Usage: Dispatched by UserInteractionWatcher on user touch events.
 * Result: Throttled KEEPALIVE_USER_INTERACTION events sent to host app every 3 seconds.
 *
 * @todo Consider moving this action to a separate actions file (e.g., BrownfieldActions.ts)
 * to separate action definitions from saga logic.
 */
export const userInteractionKeepAlive = createAction('userInteractionKeepAlive')

export const userNavigationKeepAlive = createAction<{ currentRoute: TypedRoute | undefined }>(
  'userNavigationKeepAlive'
)

/**
 * Maps Redux actions to Brownfield Bridge events.
 *
 * NOTE: Currently performs a simple Object.assign-like operation to copy action properties to event.
 * This is a temporary solution. In future PRs, each action type should be mapped case-by-case
 * to ensure proper payload structure and prevent unintended data leaks.
 */
function mapActionToEvent(action: any): BrownfieldBridgeEvent {
  // Special handling for REFRESH_AUTH_FAILURE
  if (action.type === 'REFRESH_AUTH_FAILURE' && action.error) {
    return {
      type: action.type,
      payload: {
        message: action.error.message || action.error.toString(),
        code: action.error.code,
        error: typeof action.error === 'string' ? action.error : action.error.toString(),
      },
    }
  }

  // Special handling for FULL_STARTUP_FAILURE
  if (action.type === 'FULL_STARTUP_FAILURE' && action.error) {
    return {
      type: action.type,
      payload: {
        error: action.error.error,
        code: action.error.code,
        message: action.error.message,
      },
    }
  }

  // Special handling for FETCH_EPISODE_SUCCESS - wrap episodes in payload (remove PII)
  if (action.type === 'FETCH_EPISODE_SUCCESS' && action.episodes) {
    const items: ConversationEpisode[] = action.episodes
    return {
      type: action.type,
      payload: {
        episodes: {
          items: items.map((item) => ({
            id: item.id,
            created_at: item.created_at,
            state: item.state,
            health_issue_type_id: item.health_issue_type_id,
          })),
          links: [],
        },
      },
    }
  }

  // Special handling for PATIENT_PROFILE_FETCH_SUCCESS - wrap profile in payload (remove PII)
  if (action.type === 'PATIENT_PROFILE_FETCH_SUCCESS' && action.profile) {
    return {
      type: action.type,
      payload: {
        profile: {
          id: action.profile.id,
          user_id: action.profile.user_id,
          tenant_id: action.profile.tenant_id,
        },
      },
    }
  }

  // Default behavior for other actions
  return {
    type: action.type,
    payload: action.payload,
  }
}

function nameOf<T>(
  obj: T,
  expression: (x: { [Property in keyof T]: () => string }) => () => string
): string {
  const res: { [Property in keyof T]: () => string } = {} as { [Property in keyof T]: () => string }
  Object.keys(obj).map((k) => (res[k as keyof T] = () => k))
  return expression(res)()
}

export function* watchBrownfieldFullStartupEvents() {
  const StartupWatchableActions = {
    FEATURES: take([featuresActions.setFeatures.type]),

    // Note: REFRESH_AUTH_FAILURE is forwarded for logging only
    // Native bridge should NOT treat as terminal error
    // JS layer will attempt exchange token if refresh fails
    REFRESH_AUTH: take([LoginTypes.REFRESH_AUTH_SUCCESS, LoginTypes.REFRESH_AUTH_FAILURE]),

    SCRIBE_V2_USER_FETCH: take([
      PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS,
      PatientTypes.SCRIBE_V2_USER_FETCH_FAILURE,
    ]),

    GET_NOTIFICATION_PREFERENCES: take([
      NotificationTypes.GET_NOTIFICATION_PREFERENCES_SUCCESS,
      NotificationTypes.GET_NOTIFICATION_PREFERENCES_FAILURE,
    ]),

    FETCH_EPISODE: take([PatientHistoryTypes.FETCH_EPISODE_SUCCESS]),

    PATIENT_PROFILE_FETCH: take([PatientTypes.PATIENT_PROFILE_FETCH_SUCCESS]),

    FULL_STARTUP: take([StartupTypes.FULL_STARTUP_SUCCESS, StartupTypes.FULL_STARTUP_FAILURE]),
  }

  while (true) {
    logger.debug('Waiting for FULL_STARTUP event from BrownfieldBridge')
    const fullStartupStarted = yield take(StartupTypes.FULL_STARTUP)
    const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
    if (skipTokenExchange) {
      // Startup forwarding is skipped while token exchange is disabled. Video keepalive is handled
      // separately and must still reach the host in embedded mode.
      continue
    }
    logger.debug('Forwarding action to BrownfieldBridge')
    BrownfieldBridgeInternal.triggerEvent(mapActionToEvent(fullStartupStarted))

    while (true) {
      logger.debug('Waiting for actions to forward to BrownfieldBridge')
      const action = yield race(StartupWatchableActions)
      const actions = Object.values(action)
      for (const act of actions) {
        logger.debug('Forwarding action to BrownfieldBridge', { action: act })
        BrownfieldBridgeInternal.triggerEvent(mapActionToEvent(act))
      }
      const isStartupCompleted = Object.keys(action).includes(
        nameOf(StartupWatchableActions, (src) => src.FULL_STARTUP)
      )
      if (isStartupCompleted) {
        logger.debug('FULL_STARTUP finished, stopping event forwarding to BrownfieldBridge')
        break
      }
    }
  }
}

/**
 * Watches user interaction events and triggers keep-alive events for the native bridge.
 * TODO: Expand to other user interactions as needed (navigation, form submissions, etc.) DIA-85524
 */

export function* watchBrownfieldKeepAliveUserInteractionEvents(userInteractionThrottleMs: number) {
  yield throttle(
    userInteractionThrottleMs,
    userInteractionKeepAlive.type,
    function* triggerKeepAliveUserInteractionEvent() {
      const isVideoCallActive = (yield select(selectKeepAliveIntervalId)) != null
      if (isVideoCallActive) return
      const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
      if (skipTokenExchange) return
      const event: KeepAliveUserInteraction = { type: 'KEEPALIVE_USER_INTERACTION' }
      BrownfieldBridgeInternal.triggerEvent(event)
    }
  )
}

export function* watchBrownfieldKeepAliveNavigationEvents(navigationKeepaliveIntervalMs: number) {
  yield throttle(
    navigationKeepaliveIntervalMs,
    userNavigationKeepAlive.type,
    function* triggerKeepAliveNavigationEvent(
      action: PayloadAction<{ currentRoute: TypedRoute | undefined }>
    ) {
      const isVideoCallActive = (yield select(selectKeepAliveIntervalId)) != null
      if (isVideoCallActive) return
      const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
      if (skipTokenExchange) return
      const { currentRoute } = action.payload
      const event: KeepAliveNavigation = {
        type: 'KEEPALIVE_NAVIGATION',
        payload: {
          route: currentRoute?.key ?? 'key not found',
        },
      }
      BrownfieldBridgeInternal.triggerEvent(event)
    }
  )
}

export function* watchBrownfieldKeepAliveMediaEvents() {
  while (true) {
    yield take(mediaPlaybackStarted.type)
    const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
    if (!skipTokenExchange) {
      BrownfieldBridgeInternal.startMediaKeepAlive()
    }

    yield take(mediaPlaybackStopped.type)
    if (!skipTokenExchange) {
      BrownfieldBridgeInternal.stopMediaKeepAlive()
    }
  }
}

/**
 * Watches video call lifecycle events and triggers keep-alive events for the native bridge.
 * Stores intervalId in Redux for idempotent cleanup on errors/crashes (DIA-85511).
 */
export function* watchBrownfieldKeepAliveVideoEvents(videoCallKeepaliveIntervalMs: number) {
  while (true) {
    const actionVideoCallStarted = yield take([
      videoSessionActions.onDemandCallRequested,
      videoSessionActions.patientAcceptedCall,
      videoSessionActions.saveLastVideoCallInfo,
    ])

    if (actionVideoCallStarted.type === videoSessionActions.patientAcceptedCall.type) {
      const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
      if (!skipTokenExchange) {
        continue
      }
    }

    if (actionVideoCallStarted.type === videoSessionActions.saveLastVideoCallInfo.type) {
      const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
      if (!skipTokenExchange) {
        continue
      }

      const acceptedAt: number | null = yield select(selectVideoCallAcceptedAt)
      if (acceptedAt == null) {
        continue
      }

      const existingIntervalId = yield select(selectKeepAliveIntervalId)
      if (existingIntervalId != null) {
        continue
      }
    }

    // Idempotent cleanup: clear any orphaned interval from previous crash/error before starting new one
    const existingIntervalId = yield select(selectKeepAliveIntervalId)
    if (existingIntervalId != null) {
      clearInterval(existingIntervalId)
      yield put(videoSessionActions.setKeepAliveIntervalId({ intervalId: null }))
    }

    const event: KeepAliveVideoCall = {
      type: 'KEEPALIVE_VIDEO_CALL',
    }
    BrownfieldBridgeInternal.triggerEvent(event)

    const intervalId = setInterval(() => {
      const event: KeepAliveVideoCall = {
        type: 'KEEPALIVE_VIDEO_CALL',
      }
      BrownfieldBridgeInternal.triggerEvent(event)
    }, videoCallKeepaliveIntervalMs)

    yield put(videoSessionActions.setKeepAliveIntervalId({ intervalId }))

    yield take(KEEPALIVE_CLEANUP_ACTIONS.map((a) => a.type))

    // Use local intervalId for cleanup: disconnectFromSession/resetState clears keepAliveIntervalId
    // in the reducer before the saga runs, so select() would return null and skip clearInterval.
    clearInterval(intervalId)
    yield put(videoSessionActions.setKeepAliveIntervalId({ intervalId: null }))
  }
}

/**
 * Watches video call lifecycle events and triggers keep-alive events for the native bridge.
 * Timing values are retrieved from the native bridge (or Config fallback) for dynamic configurability.
 */
export function* watchBrownfieldKeepAliveEvents() {
  const timing: KeepAliveTimingConfig = yield call(
    BrownfieldBridgeInternal.getKeepAliveTimingConfig
  )

  yield fork(watchBrownfieldKeepAliveVideoEvents, timing.videoCallKeepaliveIntervalMs)
  yield fork(watchBrownfieldKeepAliveUserInteractionEvents, timing.userInteractionThrottleMs)
  yield fork(watchBrownfieldKeepAliveNavigationEvents, timing.navigationKeepaliveIntervalMs)
  yield fork(watchBrownfieldKeepAliveMediaEvents)
}

/**
 * Watches single episode loading events.
 */
export function* watchBrownfieldFetchOneEpisodeDeeplinkEvents() {
  while (true) {
    const { episodeId, requestId } = yield take(
      PatientHistoryTypes.FETCH_ONE_EPISODE_DEEPLINK_REQUEST
    )
    const skipTokenExchange: boolean = yield select(selectSkipTokenExchange)
    if (skipTokenExchange) {
      continue
    }
    const { channelId } = yield take(PatientHistoryTypes.FETCH_ONE_EPISODE_REQUEST)
    let { episode } = yield take(PatientHistoryTypes.FETCH_ONE_EPISODE_SUCCESS)

    const module = getNativeModule()

    if (episode && channelId && channelId == episodeId && episode.id && requestId) {
      const event: FetchOneEpisodeSuccess = {
        type: 'FETCH_ONE_EPISODE_SUCCESS',
        payload: {
          episodeId: channelId,
        },
      }
      BrownfieldBridgeInternal.triggerEvent(event)
      module?.openDeeplinkCallbackFor({
        type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkOpened,
        payload: { requestId: requestId, episodeId: episodeId },
      })
    } else {
      const event: FetchOneEpisodeFailure = {
        type: 'FETCH_ONE_EPISODE_FAILURE',
        payload: {
          episodeId: channelId,
        },
      }
      BrownfieldBridgeInternal.triggerEvent(event)
      module?.openDeeplinkCallbackFor({
        type: BROWNFIELD_BRIDGE_DEEPLINK_EVENT_TYPE.DeeplinkRejected,
        payload: { requestId: requestId },
      })
    }
  }
}

/**
 * Watches all Redux actions and logs them to console if EMBEDDED_LOG_ALL_EVENTS is enabled in dev.
 * This is not supported in production environments.
 */
export function* watchBrownfieldAllEvents() {
  if (!__DEV__ || !Config.EMBEDDED_LOG_ALL_EVENTS) {
    return
  }
  let index = 0
  while (true) {
    const action = yield take('*')
    logger.debug(`DEBUG - BrownfieldBridge action ${index} received`, { action })
    index += 1
  }
}

export function* watchBrownfieldEvents() {
  yield fork(watchBrownfieldFullStartupEvents)
  yield fork(watchBrownfieldKeepAliveEvents)
  yield fork(watchBrownfieldAllEvents)
  yield fork(watchBrownfieldFetchOneEpisodeDeeplinkEvents)
}
